// ENG-2277 (HTML Forms): the one way a surface opens a digital Form (tech plan D6). It fetches the
// Form fresh, so an edit or a removed surface since the picker loaded is honoured, then opens the
// print dialog with the form filled in for this patient and surface.
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import type { FormSettings } from "components/pdf-generation-v3/form-print.utils";
import type { MergeTagExtraData } from "components/word-processor-field/merge-tags/merge-tags-values/merge-tags-values";
import { getTextWithMergeTagsValues } from "components/word-processor-field/text-templates/text-templates.utils";
import {
  ANVIL_MERGE_TAG_PARITY,
  PDF_PRINTABLE_TABLE_LAYOUT_FIXES,
  VERTICAL_SOUTHWESTERN_LIMITATIONS
} from "constants/feature-flags";
import { fitOnOnePage } from "features/settings/templates/template/form-settings-panel/fit-to-page.util";
import {
  type FormPrintOptions,
  renderPageCount
} from "features/settings/templates/template/form-settings-panel/render-page-count.util";
import {
  DIGITAL_FORM_OPEN_QUERY,
  textTemplatesApi,
  useLazyGetTextTemplateQuery
} from "services/text-templates";
import type { FormTemplate } from "services/types/form-templates.type";
import type { TextTemplate } from "services/types/text-templates.type";

import {
  blankUnresolvedCurrentAppointmentTags,
  findBlankMergeTags
} from "./blank-merge-tags.util";
import {
  type CustomPrintFormMetadata,
  useOptionalCustomPrintFormPreview
} from "./use-custom-print-form-preview.hook";

export const FORM_UNAVAILABLE_MESSAGE = "This form is no longer available";
const FORM_LOAD_FAILED_MESSAGE =
  "We couldn't open this form. Please try again.";

type OpenDigitalFormParams = {
  /** The picker row (see components/form-templates-field/digital-forms.utils). */
  option: FormTemplate;
  /** The surface opening it (a FormTemplateType). */
  placement: number;
  patientId: number;
  getMergeTagValue: (
    mergeTag: string,
    options?: Record<string, unknown>
  ) => string | null | undefined;
  /** The surface's own values, e.g. { request } or { order } (S3 context tags). */
  additionalMergeTagsValues?: MergeTagExtraData;
  metadata?: Omit<
    CustomPrintFormMetadata,
    "additionalMergeTagsValues" | "formSettings"
  >;
  /** The form was deleted or taken off this surface: the caller clears its selection. */
  onUnavailable?: () => void;
};

const isNotFound = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "status" in error &&
  error.status === 404;

// The form shrunk onto one page, or as it is when it cannot fit. Its pages are counted as the print
// dialog prints them (`print`), or a form that measures one page here can print on two.
const fitOnPrintedPage = async (
  html: string,
  formSettings: FormSettings,
  print?: FormPrintOptions
) =>
  (
    await fitOnOnePage(html, formSettings, (stepHtml, settings) =>
      renderPageCount(stepHtml, settings, print)
    )
  ).html;

/**
 * The filled-in form shrunk onto one page when the form asks for it (its print_defaults): a purchase
 * agreement grows with its sale's rows. It is fitted before the dialog opens, which takes its content
 * as it opens. A form that cannot fit, or cannot be measured, prints as it is.
 */
export const fitForPrint = async (
  html: string,
  formSettings: FormSettings,
  print?: FormPrintOptions
) => {
  try {
    return await fitOnPrintedPage(html, formSettings, print);
  } catch {
    return html;
  }
};

// Fitting renders the form's PDF up to seven times on the main thread, and a form that cannot fit
// pays for all seven to come back unchanged. The same filled-in form, printed the same way, fits
// the same way, so a surface keeps its last results: the form as it opened, shrunk or not. They are
// a patient's filled-in form, so they are the surface's own and go when it does.
const FIT_CACHE_LIMIT = 20;
type FitCache = Map<string, string>;

const fitForOpen = async (
  fitCache: FitCache,
  template: Pick<TextTemplate, "id" | "updated_at">,
  html: string,
  formSettings: FormSettings,
  print: FormPrintOptions
) => {
  // Everything the page count depends on: the content, the form's page and how the dialog prints.
  const key = JSON.stringify([
    template.id,
    template.updated_at,
    formSettings,
    print,
    html
  ]);
  const remembered = fitCache.get(key);
  if (remembered !== undefined) return remembered;

  try {
    const fitted = await fitOnPrintedPage(html, formSettings, print);
    if (fitCache.size >= FIT_CACHE_LIMIT) {
      // A Map keeps the order its keys were added in, so the first is the oldest.
      fitCache.delete(fitCache.keys().next().value as string);
    }
    fitCache.set(key, fitted);
    return fitted;
  } catch {
    // Not measured, so not remembered: the next open measures again.
    return html;
  }
};

export const useOpenDigitalForm = () => {
  const dispatch = useAppDispatch();
  const flags = useFlags();
  // A count, not a flag: a call overtaken by a newer one is still fitting when the newer one ends.
  const [fitsRunning, setFitsRunning] = useState(0);
  const [fitCache] = useState<FitCache>(() => new Map());
  // Optional: a surface mounts the print dialog's provider only with the flag on, and this hook is
  // called unconditionally.
  const preview = useOptionalCustomPrintFormPreview();
  const [fetchTemplate, { isFetching }] = useLazyGetTextTemplateQuery();

  // Fetching and fitting take a while, and the surface stays live meanwhile: its invoice or its form
  // can change, or it can close. Each call takes the next number, and one that is no longer the
  // latest when its wait ends opens nothing, so the dialog never shows an earlier call's values.
  const latestCallRef = useRef(0);
  useEffect(
    () => () => {
      // A surface that has gone overtakes whatever was still on its way.
      latestCallRef.current += 1;
    },
    []
  );

  // For a surface whose values changed under a call still on its way (another invoice chosen): the
  // form was filled from the old ones, so it must not open.
  const cancelOpening = useCallback(() => {
    latestCallRef.current += 1;
  }, []);

  const notify = useCallback(
    (message: string) => {
      dispatch(setCustomMessage(message));
      dispatch(setOpen(true));
    },
    [dispatch]
  );

  const openDigitalForm = useCallback(
    async ({
      option,
      placement,
      patientId,
      getMergeTagValue,
      additionalMergeTagsValues,
      metadata,
      onUnavailable
    }: OpenDigitalFormParams): Promise<boolean> => {
      if (!preview) {
        console.error(
          "useOpenDigitalForm: no CustomPrintFormPreviewProvider above this surface"
        );
        return false;
      }
      const {
        setPatientId,
        setTemplate,
        setMetadata,
        setFormContent,
        showDialog
      } = preview;
      latestCallRef.current += 1;
      const call = latestCallRef.current;
      const isOvertaken = () => call !== latestCallRef.current;

      const id = Number(option.text_template_meta?.id);
      const { data: template, error } = await fetchTemplate({
        id,
        query: DIGITAL_FORM_OPEN_QUERY
      });
      // Before anything is said or cleared: `onUnavailable` would clear the newer call's choice.
      if (isOvertaken()) return false;

      const unavailable =
        isNotFound(error) ||
        (template && !(template.placements ?? []).includes(placement));
      if (unavailable) {
        notify(FORM_UNAVAILABLE_MESSAGE);
        // The picker's list is stale: refetch it.
        dispatch(
          textTemplatesApi.util.invalidateTags([
            { type: "TextTemplates", id: "LIST" }
          ])
        );
        onUnavailable?.();
        return false;
      }
      if (!template) {
        notify(FORM_LOAD_FAILED_MESSAGE);
        return false;
      }

      // The values this form prints (SN-19877, D4b: an unresolved Current Appointment tag prints blank).
      const formValue = flags[ANVIL_MERGE_TAG_PARITY]
        ? blankUnresolvedCurrentAppointmentTags(getMergeTagValue)
        : getMergeTagValue;
      const filled = getTextWithMergeTagsValues({
        text: template.text ?? "",
        getMergeTagValue: formValue,
        insertMergeTagValues: true,
        allowHtml: true
      });
      const formSettings = template.form_settings ?? null;
      const asks = formSettings?.print_defaults;
      let content = filled;
      if (formSettings && asks?.fit_one_page) {
        setFitsRunning((count) => count + 1);
        try {
          // As the dialog will print it: the same reads its PDF makes (use-custom-print-form-preview).
          content = await fitForOpen(fitCache, template, filled, formSettings, {
            pdfNativeTablesEnabled: Boolean(
              preview.nativePdfTablesEnabled ||
                flags[VERTICAL_SOUTHWESTERN_LIMITATIONS]
            ),
            printableTableLayoutFixesEnabled: Boolean(
              flags[PDF_PRINTABLE_TABLE_LAYOUT_FIXES]
            ),
            signatureFooter: Boolean(
              asks.patient_signature_in_footer ||
                (preview.providerSignatureEnabled &&
                  asks.provider_signature_in_footer)
            )
          });
        } finally {
          setFitsRunning((count) => count - 1);
        }
        if (isOvertaken()) return false;
      }

      setPatientId(patientId);
      setTemplate({ ...option, text_template_meta: template });
      setMetadata((previous) => ({
        ...previous,
        ...metadata,
        additionalMergeTagsValues,
        formSettings,
        blankMergeTags: findBlankMergeTags(template.text ?? "", formValue)
      }));
      showDialog();
      setFormContent(content);
      return true;
    },
    [dispatch, fetchTemplate, fitCache, flags, notify, preview]
  );

  return {
    openDigitalForm,
    cancelOpening,
    isOpening: isFetching || fitsRunning > 0
  };
};
